import { createSelector } from 'reselect';

import type Carrier from '~/types/Carrier';
import type CarrierAccount from '~/types/CarrierAccount';

import { CANADA_POST, CARRIER_MANIFEST_ABOUT_COPY, UPS } from '~/constants/carriers';
import {
  type CarrierAccountRegStatusMap,
  type CarrierModalStatus,
} from '~/reducers/carrier-accounts/carrier-accounts.reducer';
import { RegStatus, type IncompleteRegStatus } from '~/types/CarrierAccountRegStatus';
import { extractRefreshableLastValue } from '~/utils/fetchable';

import { type ApplicationState } from '../store';

const getCarrierAccountsStore = (state: ApplicationState) => state.carrierAccounts;

export const getCarrierAccounts = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): readonly CarrierAccount[] => carrierAccountsStore.carrierAccounts,
);

export const getCarrierAccountByObjectId = (objectId: string) =>
  createSelector(
    getCarrierAccounts,
    (carrierAccounts): Maybe<CarrierAccount> =>
      carrierAccounts.find((carrierAccount) => carrierAccount.objectId === objectId) || null,
  );

export const getCarrierAccountAboutInfoByObjectId = (objectId: string) =>
  createSelector(
    getCarrierAccountByObjectId(objectId),
    (
      carrierAccount,
    ): {
      copyKey: string;
      image: string;
      name: string;
    } => {
      const image = carrierAccount?.carrier?.img260 || '';
      const name = carrierAccount?.carrier?.name || '';
      const id = carrierAccount?.carrier?.id;
      const copyKey = (id && CARRIER_MANIFEST_ABOUT_COPY[id]) || '';

      return {
        copyKey,
        image,
        name,
      };
    },
  );

export const getHasCarrierAboutInfoByObjectId = (objectId: string) =>
  createSelector(getCarrierAccountByObjectId(objectId), (carrierAccount): boolean => {
    const id = carrierAccount?.carrier?.id;
    const copyKey = id && CARRIER_MANIFEST_ABOUT_COPY[id];

    return Boolean(copyKey);
  });

export const getCarrierAccountRegStatusMap = createSelector(
  getCarrierAccountsStore,
  (carrierAccountStore): Refreshable<CarrierAccountRegStatusMap, string> =>
    carrierAccountStore.carrierAccountRegStatusMap,
);

export const getExtractedCarrierAccountRegStatusMap = createSelector(
  getCarrierAccountRegStatusMap,
  (carrierAccountRegStatusMap): CarrierAccountRegStatusMap =>
    extractRefreshableLastValue(carrierAccountRegStatusMap) || {},
);

export const getMasterCarrierAccounts = createSelector(
  getCarrierAccounts,
  getExtractedCarrierAccountRegStatusMap,
  (carrierAccounts, carrierAccountRegStatusMap): readonly CarrierAccount[] =>
    carrierAccounts.filter((carrierAccount) =>
      // Exclude master accounts that have been opted out
      carrierAccountRegStatusMap[carrierAccount.objectId!]
        ? carrierAccount.isShippoAccount &&
          carrierAccountRegStatusMap[carrierAccount.objectId!] !== RegStatus.OPTED_OUT
        : carrierAccount.isShippoAccount,
    ),
);

export const getUserCarrierAccounts = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): readonly CarrierAccount[] =>
    carrierAccountsStore.carrierAccounts.filter(
      (carrierAccount) => !carrierAccount.isShippoAccount,
    ),
);

export const getCarrierAccountsObjectIdMap = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): { [objectId: string]: CarrierAccount } => {
    const objectIdMap: { [objectId: string]: CarrierAccount } = {};
    carrierAccountsStore.carrierAccounts.map((carrierAccount) => {
      if (carrierAccount.objectId) objectIdMap[carrierAccount.objectId] = carrierAccount;
    });

    return objectIdMap;
  },
);

export const getIsEligibleForUpsMasterAccount = createSelector(
  getCarrierAccountsStore,
  // This really only means the user is eligible for the master account, because it could be in a
  // registration state that is not fully registered.
  (carrierAccountsStore): boolean => hasMasterAccount(carrierAccountsStore.carrierAccounts, UPS),
);

export const getHasUpsMarketplaceOrMasterAcc = createSelector(
  getCarrierAccounts,
  getExtractedCarrierAccountRegStatusMap,
  (carrierAccounts, carrierAccountRegStatusMap): boolean =>
    hasCarrierAccountWithParameter(carrierAccounts, 'billing_address_street1') ||
    hasRegisteredMasterAccount(carrierAccounts, UPS, carrierAccountRegStatusMap), // or if user has a fully registered UPS Master account
);

export const getHasCanadaPostMasterAcc = createSelector(
  getCarrierAccounts,
  getExtractedCarrierAccountRegStatusMap,
  (carrierAccounts, carrierAccountRegStatusMap): boolean =>
    hasRegisteredMasterAccount(carrierAccounts, CANADA_POST, carrierAccountRegStatusMap),
);

export const hasEBayCanadaPostAccount = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): boolean =>
    hasCarrierAccountWithParameter(carrierAccountsStore.carrierAccounts, 'platform_id_key', 'eBay'),
);

export const getCarrierAccountRegStatusesIncomplete = createSelector(
  getExtractedCarrierAccountRegStatusMap,
  (
    carrierAccountsRegStatuses: CarrierAccountRegStatusMap,
  ): { [carrierAccountObjectId: string]: IncompleteRegStatus } =>
    // Iterate through all carrier account reg statuses in the store and return the ones that do not have COMPLETE status
    Object.keys(carrierAccountsRegStatuses).reduce(
      (results: { [key: string]: IncompleteRegStatus }, objectId: string) => {
        if (carrierAccountsRegStatuses[objectId] !== RegStatus.COMPLETE) {
          // Casting to IncompleteRegStatus since we filter out COMPLETE in the if condition
          results[objectId] = carrierAccountsRegStatuses[objectId] as IncompleteRegStatus;
        }
        return results;
      },
      {},
    ),
);

export const getIsFetchingCarrierAccounts = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): boolean => carrierAccountsStore.isFetchingCarrierAccounts,
);

export const getIsSavingCarrierAccount = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): boolean => carrierAccountsStore.isSavingCarrierAccount,
);

export const getCarrierAccountsErrorMessage = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): string => carrierAccountsStore.errorMessage,
);

export const getSuccessfulSubmit = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): Maybe<CarrierAccount> => carrierAccountsStore.successfulSubmit,
);

export const getSuccessfulFetch = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): boolean => carrierAccountsStore.successfulFetch,
);

export const getEditingCarrierAccountId = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): Maybe<string> => carrierAccountsStore.editingCarrierAccountId,
);

export const getModalStatus = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): CarrierModalStatus => carrierAccountsStore.modalStatus,
);

export const getUserAction = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): 'connect' | 'edit' | 'none' => carrierAccountsStore.userAction || 'none',
);

export const getEditCarrierServiceLevelsError = createSelector(
  getCarrierAccountsStore,
  (carrierAccountsStore): string[] => carrierAccountsStore.editingCarrierServiceLevelsError,
);

// Helper functions

/*
  Description:
    Returns true if there is an account that contains the specified parameter

  Parameters:
    - carrierAccounts: list of carrier accounts to search through
    - parameterKey: name of the parameter key
    - parameterValue (optional): the value that the parameter must contain (if specified)
*/
const hasCarrierAccountWithParameter = (
  carrierAccounts: readonly CarrierAccount[],
  parameterKey: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  parameterValue?: any,
) =>
  carrierAccounts.some(
    (account) =>
      account.parameters &&
      account.parameters[parameterKey] &&
      (parameterValue === undefined || account.parameters[parameterKey] === parameterValue),
  );

/*
  Description:
    Returns true if there is a master account for the given carrier

  Parameters:
    - carrierAccounts: list of carrier accounts to search through
    - carrier: Carrier desired
*/
const hasMasterAccount = (carrierAccounts: readonly CarrierAccount[], carrier: Carrier) =>
  carrierAccounts.some((account) => account.isShippoAccount && account.carrier.id === carrier.id);

/*
  Description:
    Returns true if there is a master account for the given carrier that is fully registered (RegStatus.COMPLETE)

  Parameters:
    - carrierAccounts: list of carrier accounts to search through
    - carrier: Carrier desired
    - regStatuses: list of registration statuses, if any
*/
const hasRegisteredMasterAccount = (
  carrierAccounts: readonly CarrierAccount[],
  carrier: Carrier,
  regStatuses: CarrierAccountRegStatusMap,
) =>
  carrierAccounts.some(
    (account) =>
      (account.isShippoAccount &&
        account.carrier.id === carrier.id &&
        account.objectId &&
        regStatuses[account.objectId!] === RegStatus.COMPLETE) ||
      regStatuses[account.objectId!] === RegStatus.OPTED_OUT,
  );
